import { CONSTANTS } from "~/helpers/constants";
import { css } from "~/utils/css";

import type { Content } from "~/types";
import { logCookiedockError } from "~/helpers";

export async function injectCookiedockCSS(content: Content) {
  const style = await import("../main.scss?inline");

  try {
    const styleElement: HTMLStyleElement = document.createElement("style");
    styleElement.setAttribute("name", CONSTANTS.BANNER.SELECTOR);

    const dynamicCSS = generateDynamicCSS(content);
    styleElement.textContent = style.default + dynamicCSS;

    document.head.appendChild(styleElement);
  } catch (error) {
    logCookiedockError(CONSTANTS.CONSOLE.UNABLE_TO_INSERT_CSS, JSON.stringify(error));
  }
}

function generateDynamicCSS(content: Content): string {
  const defaultPrimaryColor = "#10a2ab";
  const defaultSecondaryColor = "#dcdcdc";

  if (!content.appearance.colors) {
    content.appearance.colors = {
      primary: defaultPrimaryColor,
      secondary: defaultSecondaryColor,
    };
  }

  const primaryColor = css.isValidHex(content.appearance.colors.primary) ? content.appearance.colors.primary : defaultPrimaryColor;
  const secondaryColor = css.isValidHex(content.appearance.colors.secondary) ? content.appearance.colors.secondary : defaultSecondaryColor;

  const textColor = css.isDarkColor(primaryColor) ? "#ffffff" : "#000000";

  // Calculate derived colors
  const primaryLight = css.lighten(primaryColor, 10);
  const primaryDark = css.darken(primaryColor, 15);
  const secondaryLight = css.lighten(secondaryColor, 15);
  const secondaryDark = css.darken(secondaryColor, 15);

  return `
    :root {
      /* Primary Colors */
      --cookiedock-primary: ${primaryColor};
      --cookiedock-primary-light: ${primaryLight};
      --cookiedock-primary-dark: ${primaryDark};
      
      /* Secondary Colors */
      --cookiedock-secondary-color: ${secondaryColor};
      --cookiedock-secondary-light: ${secondaryLight};
      --cookiedock-secondary-dark: ${secondaryDark};
      
      /* Text and Background Colors */
      --cookiedock-background: #ffffff;
      --cookiedock-text: ${textColor};
      --cookiedock-text-color: ${textColor};
      
      /* Border and Shadow Colors */
      --cookiedock-border: rgba(195, 195, 195, 0.2);
      --cookiedock-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
      
      /* Interactive States */
      --cookiedock-primary-hover: ${css.hex2rgba(primaryColor, 0.9)};
      --cookiedock-primary-shadow: ${css.hex2rgba(primaryColor, 0.3)};
      --cookiedock-primary-shadow-hover: ${css.hex2rgba(primaryColor, 0.4)};
      --cookiedock-secondary-hover: ${css.hex2rgba(secondaryColor, 0.1)};
      --cookiedock-light-hover: ${css.lighten(primaryColor, 80)};
      --cookiedock-light-hover-75: ${css.lighten(primaryColor, 75)};
      
      /* Focus and Accessibility */
      --cookiedock-focus-ring: ${css.hex2rgba(primaryColor, 0.4)};
      --cookiedock-backdrop: rgba(0, 0, 0, 0.05);
    }
  `;
}
